import { useParams } from 'react-router-dom';
import { useAuthContext } from 'contexts/AuthContext';
import { useGetDealById } from 'views/Deals/hooks/useGetDealById';
import { useGetExtractedProForma } from 'queries/ExtractedProForma';
import RenderIf from 'components/RenderIf/RenderIf';
import EmptyState from 'components/EmptyState/EmptyState';
import { useMapCoordinates } from 'hooks/useMapCoordinates';
import ExecutiveSummaryContent from 'views/ProFormaTable/components/ExecutiveSummary/ExecutiveSummaryContent/ExecutiveSummaryContent';
import ExecutiveSummaryGenericTable from 'views/ProFormaTable/components/ExecutiveSummary/ExecutiveSummaryGenericTable/ExecutiveSummaryGenericTable';
import ExecutiveSummaryTimelineChart from 'views/ProFormaTable/components/ExecutiveSummary/ExecutiveSummaryTimelineChart/ExecutiveSummaryTimelineChart';
import { buildBlocks } from 'views/ProFormaTable/components/ExecutiveSummary/layout/buildBlocks';
import { extractedResolvers } from 'views/ProFormaTable/components/ExecutiveSummary/resolvers/extracted';
import ExtractedProFormaSideNav from './ExtractedProFormaSideNav/ExtractedProFormaSideNav';
import { EXTRACTION_INCOMPLETE_TITLE } from './constants';
import { buildTimelineData, buildTimelineSections, extractedAddress } from './helpers';

const ExtractedProForma = () => {
    const { selectedOrganizationId } = useAuthContext();
    const { dealId, extractedProFormaId } = useParams<{ dealId: string; extractedProFormaId: string }>();

    const { data: deal } = useGetDealById({
        orgId: selectedOrganizationId!,
        dealId: Number(dealId),
        shouldFetch: !!Number(dealId),
    });

    const { data: extractedProForma, isLoading } = useGetExtractedProForma({
        organizationId: selectedOrganizationId!,
        dealId: Number(dealId),
        extractedProFormaId: Number(extractedProFormaId),
    });

    const fields = extractedProForma?.extractedData?.extracted ?? [];
    const address = extractedAddress(fields);
    const { lat, lng, coordinates } = useMapCoordinates(address ? {
        address, city: '', state: '', zipCode: '', latitude: undefined, longitude: undefined,
    } : null);

    const blocks = buildBlocks({ proFormaModelId: extractedProForma?.proFormaModelId ?? 0 }, extractedResolvers(fields));
    const timelineData = buildTimelineData(fields, extractedProForma?.fileName ?? '');
    const timelineSections = timelineData ? [] : buildTimelineSections(fields);

    const isReady = extractedProForma?.extractionStatus === 'SUCCEEDED' && extractedProForma.extractedData != null;

    return (
        <RenderIf isTrue={!isLoading} isLoading={isLoading}>
            <RenderIf isTrue={!!extractedProForma && isReady}>
                {extractedProForma && (
                    <div className="d-flex align-items-stretch">
                        <div className="flex-shrink-0">
                            <ExtractedProFormaSideNav dealName={deal?.dealName} extractedProForma={extractedProForma} />
                        </div>
                        <div className="flex-grow-1 overflow-hidden ml-3">
                            <ExecutiveSummaryContent
                                subtitle={extractedProForma.fileName}
                                blocks={blocks}
                                map={coordinates ? { lat, lng } : null}
                                timeline={timelineData
                                    ? <ExecutiveSummaryTimelineChart data={[timelineData]} />
                                    : timelineSections.length > 0 && (
                                        <>
                                            <ExecutiveSummaryGenericTable title="TIMELINE" sections={timelineSections} />
                                            <div className="mb-2" />
                                        </>
                                    )}
                            />
                        </div>
                    </div>
                )}
            </RenderIf>
            <RenderIf isTrue={!!extractedProForma && !isReady}>
                <EmptyState title={EXTRACTION_INCOMPLETE_TITLE} />
            </RenderIf>
        </RenderIf>
    );
};

export default ExtractedProForma;
